import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import { TicketFilters } from '../hooks/useTicketFilters';

interface ActiveFilterChipsProps {
  filters: TicketFilters;
  onRemoveFilter: (key: string, value?: string) => void;
  onClearAll: () => void;
}

interface ChipData {
  key: string;
  label: string;
  value?: string;
}

function buildChips(filters: TicketFilters): ChipData[] {
  const chips: ChipData[] = [];

  for (const status of filters.statuses) {
    chips.push({ key: 'statuses', label: `Status = ${status}`, value: status });
  }
  for (const status of filters.excludeStatuses) {
    chips.push({ key: 'excludeStatuses', label: `Status ≠ ${status}`, value: status });
  }
  for (const sev of filters.severities) {
    chips.push({ key: 'severities', label: `Severity = ${sev.replace('_', ' ')}`, value: sev });
  }
  for (const sev of filters.originalSeverities) {
    chips.push({ key: 'originalSeverities', label: `Orig. Severity = ${sev.replace('_', ' ')}`, value: sev });
  }
  if (filters.assignee) {
    const display = filters.assignee === '__unassigned__' ? 'Unassigned' : filters.assignee;
    chips.push({ key: 'assignee', label: `Assignee = ${display}` });
  }
  if (filters.dateRange) {
    let rangeLabel: string;
    if (filters.dateRange.type === 'relative') {
      rangeLabel = `Last ${filters.dateRange.amount} ${filters.dateRange.unit}${filters.dateRange.amount > 1 ? 's' : ''}`;
    } else {
      rangeLabel = `${filters.dateRange.startDate} to ${filters.dateRange.endDate}`;
    }
    chips.push({ key: 'dateRange', label: `Date: ${rangeLabel}` });
  }
  if (filters.ticketIds.length) {
    const label = filters.themeTitle
      ? `Theme: ${filters.themeTitle} (${filters.ticketIds.length} tickets)`
      : `${filters.ticketIds.length} specific tickets`;
    chips.push({ key: 'ticketIds', label });
  }

  return chips;
}

function FilterChip({ label, onDismiss }: { label: string; onDismiss: () => void }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '16px',
        border: '1px solid #0972d3',
        backgroundColor: '#f0f9ff',
        fontSize: '14px',
        color: '#0972d3',
        fontWeight: 700,
        lineHeight: '20px',
      }}
    >
      {label}
      <button
        onClick={onDismiss}
        aria-label={`Remove filter: ${label}`}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0 2px',
          color: '#0972d3',
          fontSize: '16px',
          lineHeight: 1,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        ×
      </button>
    </span>
  );
}

export function ActiveFilterChips({ filters, onRemoveFilter, onClearAll }: ActiveFilterChipsProps) {
  const chips = buildChips(filters);

  if (chips.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      {chips.map((chip, i) => (
        <FilterChip
          key={`${chip.key}-${chip.value ?? i}`}
          label={chip.label}
          onDismiss={() => onRemoveFilter(chip.key, chip.value)}
        />
      ))}
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={onClearAll}>Clear filters</Button>
        <Button variant="link" disabled>Save filters</Button>
      </SpaceBetween>
    </div>
  );
}
